import { ReactElement, useState, useCallback } from 'react'
import { PopoverProps, Popover } from '@mui/material'
import { motion } from 'framer-motion'
import { Prompt, useHistory } from 'react-router-dom'
import { Button } from '@components/v2/button'
import { t } from '@lingui/core/macro'

interface OverlayProps {
  children: ReactElement
  popoverProps: PopoverProps
  delay?: number
}

const Overlay = ({ popoverProps, children }: OverlayProps) => {
  const { anchorEl, anchorOrigin, transformOrigin, open, onClose, id, ...rest } = popoverProps
  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      onClose={onClose}
      id={id}
      sx={{
        padding: 10,
        '.MuiPaper-root': {
          boxShadow:
            '0px 12px 12px rgba(48, 80, 90, 0.04), 0px 4px 4px rgba(48, 80, 90, 0.12), 0px 24px 24px -12px rgba(48, 80, 90, 0.04)',
          boxSizing: 'border-box',
          borderRadius: '8px',
        },
      }}
      {...rest}
    >
      {children}
    </Popover>
  )
}

interface BentoGridAddProps {
  className?: string
}

const BentoGridAdd = ({ className = '' }: BentoGridAddProps) => {
  return (
    <svg viewBox="0 0 518 173" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <defs>
        <radialGradient id="fadeGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
          <stop offset="0%" stopColor="#F7FAFE" stopOpacity="1" />
          <stop offset="100%" stopColor="#F7FAFE" stopOpacity="0" />
        </radialGradient>

        <mask id="radialMask">
          <rect width="518" height="173" fill="url(#fadeGradient)" />
        </mask>
      </defs>

      <mask id="path-1-inside-1_4303_3780" fill="white">
        <path d="M0 0H518V173H0V0Z" />
      </mask>

      <g mask="url(#radialMask)">
        <path d="M0 0H518V173H0V0Z" fill="#F7FAFE" />
        <path d="M518 172H0V174H518V172Z" fill="#E5ECF4" mask="url(#path-1-inside-1_4303_3780)" />
        <rect x="17.5" y="24.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="17.5"
          y="24.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="347.5" y="24.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="347.5"
          y="24.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="182.5" y="24.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="182.5"
          y="24.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="17.5" y="69.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="17.5"
          y="69.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="182.5" y="69.5" width="153" height="34" rx="5.5" fill="#EFFBF5" />
        <rect x="182.5" y="69.5" width="153" height="34" rx="5.5" stroke="#5DD39E" />
        <rect x="193" y="82" width="47" height="10" rx="4" fill="#BEEDD8" />
        <rect x="250" y="82" width="75" height="10" rx="4" fill="#BEEDD8" />
        <rect x="347.5" y="69.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="347.5"
          y="69.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="17.5" y="114.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="17.5"
          y="114.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="347.5" y="114.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="347.5"
          y="114.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
        <rect x="182.5" y="114.5" width="153" height="34" rx="5.5" fill="#EFF8FD" />
        <rect
          x="182.5"
          y="114.5"
          width="153"
          height="34"
          rx="5.5"
          stroke="#A9C5D0"
          strokeDasharray="4 4"
        />
      </g>
    </svg>
  )
}

export const PromptSavedChanges = ({ hasUnsavedChanges }) => {
  const history = useHistory()
  const [state, setState] = useState<{
    showPrompt: boolean
    lastLocation: Location | null
    confirmedNavigation: boolean
    currentPath: string | null
  }>({
    showPrompt: false,
    lastLocation: null,
    confirmedNavigation: false,
    currentPath: null,
  })
  const { showPrompt, lastLocation, confirmedNavigation, currentPath } = state

  const handleBlockedNavigation = useCallback(
    nextLocation => {
      // Allow navigation if confirmed
      if (!confirmedNavigation && hasUnsavedChanges) {
        setState(prevState => ({
          ...prevState,
          showPrompt: true,
          lastLocation: nextLocation,
          currentPath: history.location.pathname,
        }))
        return false
      }
      return true
    },
    [hasUnsavedChanges, history.location.pathname, confirmedNavigation],
  )

  const handleConfirmation = useCallback(() => {
    if (lastLocation?.pathname) {
      // First set confirmed to true
      setState(prevState => ({
        ...prevState,
        showPrompt: false,
        confirmedNavigation: true,
      }))

      // Use setTimeout to ensure state is updated before navigation
      setTimeout(() => {
        history.push(lastLocation?.pathname)
        // Reset confirmation after navigation
        setState(prevState => ({
          ...prevState,
          showPrompt: false,
          confirmedNavigation: false,
          lastLocation: null,
          currentPath: null,
        }))
      }, 0)
    }
  }, [history, lastLocation?.pathname])

  const handleCancel = useCallback(() => {
    // Clicking on the back button changes the URL, if this action is cancelled,
    // we need to revert the URL change.
    if (currentPath !== null) {
      history.push(currentPath)
    }

    // Reset the state
    setState(prevState => ({
      ...prevState,
      showPrompt: false,
      confirmedNavigation: false,
      lastLocation: null,
      currentPath: null,
    }))
  }, [history, currentPath])

  return (
    <>
      <Prompt when={hasUnsavedChanges} message={handleBlockedNavigation} />
      <Overlay
        popoverProps={{
          open: showPrompt,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0, transition: { duration: 0.3 } }}
        >
          <div className="h-[100px] overflow-hidden">
            <BentoGridAdd className="translate-y-[calc(50px_+_-50%)]" />
          </div>
          <div className="flex flex-col gap-[15px] p-[30px] rounded-b-[8px] border-t-[1px] border-cv-[#E5ECF4]">
            <div className="flex flex-col gap-[4px]">
              <h2 className="text-[18px] font-bold">
                {t`You are about to lose any unsaved changes by leaving this page.`}
              </h2>
              <p className="">{t`Are you sure you want to continue?`}</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" type="button" onClick={handleCancel}>
                {t`Cancel`}
              </Button>
              <Button variant="dark" type="button" onClick={handleConfirmation}>
                {t`Confirm`}
              </Button>
            </div>
          </div>
        </motion.div>
      </Overlay>
    </>
  )
}
